import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaWhatsapp, FaCopy, FaFileExport, FaPhone } from 'react-icons/fa';
import { API_BASE } from '../../config/api';
import './AdminContacts.css';

const COPY = {
  ar: {
    title: 'جهات الاتصال',
    intro: 'أرقام الجوال من الحجوزات، بدون تكرار، لإرسال المنشورات والإعلانات.',
    lang: 'English',
    search: 'بحث',
    source: 'المصدر',
    allSources: 'كل المصادر',
    booking: 'حجز',
    from: 'من تاريخ',
    to: 'إلى تاريخ',
    booked: 'الحجز',
    anyBooking: 'الكل',
    hasBooked: 'لديه حجز',
    noBooking: 'بدون حجز',
    validity: 'الصلاحية',
    all: 'الكل',
    valid: 'صالح',
    invalid: 'غير صالح',
    optOut: 'إيقاف الرسائل',
    optedIn: 'مشترك',
    optedOut: 'ملغى',
    phone: 'الجوال',
    name: 'الاسم',
    bookings: 'الحجوزات',
    lastSeen: 'آخر ظهور',
    actions: 'إجراءات',
    copySelected: 'نسخ المحدد',
    copyAll: 'نسخ الكل',
    exportCsv: 'تصدير CSV',
    whatsapp: 'واتساب',
    empty: 'لا توجد أرقام مطابقة.',
    loading: 'جاري التحميل...',
    copied: 'تم النسخ',
    page: 'صفحة',
    of: 'من',
    prev: 'السابق',
    next: 'التالي',
    sort: 'الترتيب',
  },
  en: {
    title: 'Contacts',
    intro: 'Phone numbers from bookings, with duplicates removed, for posts and ads.',
    lang: 'العربية',
    search: 'Search',
    source: 'Source',
    allSources: 'All sources',
    booking: 'Booking',
    from: 'From',
    to: 'To',
    booked: 'Booking',
    anyBooking: 'Any',
    hasBooked: 'Has booked',
    noBooking: 'Has not booked',
    validity: 'Validity',
    all: 'All',
    valid: 'Valid',
    invalid: 'Invalid',
    optOut: 'Unsubscribe',
    optedIn: 'Subscribed',
    optedOut: 'Opted out',
    phone: 'Phone',
    name: 'Name',
    bookings: 'Bookings',
    lastSeen: 'Last seen',
    actions: 'Actions',
    copySelected: 'Copy selected',
    copyAll: 'Copy all',
    exportCsv: 'Export CSV',
    whatsapp: 'WhatsApp',
    empty: 'No matching numbers.',
    loading: 'Loading...',
    copied: 'Copied',
    page: 'Page',
    of: 'of',
    prev: 'Previous',
    next: 'Next',
    sort: 'Sort',
  },
};

const EMPTY_FILTERS = {
  q: '',
  source: 'all',
  from: '',
  to: '',
  booked: 'all',
  valid: 'all',
  opted: 'all',
  sort: 'lastSeen',
  dir: 'desc',
};

const AdminContacts = () => {
  const [lang, setLang] = useState('ar');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState({});
  const [notice, setNotice] = useState('');
  const limit = 25;
  const t = COPY[lang];

  const authHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const params = useMemo(
    () => ({
      ...filters,
      page,
      limit,
    }),
    [filters, page]
  );

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    axios
      .get(`${API_BASE}/api/admin/contacts`, { params, headers: authHeaders() })
      .then((res) => {
        setContacts(res.data.contacts || []);
        setTotal(res.data.total || 0);
      })
      .catch((err) => {
        setContacts([]);
        setTotal(0);
        setError(err.response?.data?.error || 'Error');
      })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => {
    setPage(1);
    setSelected({});
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const copyPhones = async (phones) => {
    const text = phones.filter(Boolean).join('\n');
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setNotice(t.copied);
  };

  const copyAll = async () => {
    const res = await axios.get(`${API_BASE}/api/admin/contacts`, {
      params: { ...filters, page: 1, limit: 200 },
      headers: authHeaders(),
    });
    const pages = Math.ceil((res.data.total || 0) / 200);
    let rows = res.data.contacts || [];
    for (let next = 2; next <= pages; next += 1) {
      const more = await axios.get(`${API_BASE}/api/admin/contacts`, {
        params: { ...filters, page: next, limit: 200 },
        headers: authHeaders(),
      });
      rows = rows.concat(more.data.contacts || []);
    }
    const phones = rows
      .filter((row) => row.valid && !row.optedOut)
      .map((row) => row.phone);
    await copyPhones(phones);
  };

  const exportCsv = async () => {
    const res = await axios.post(`${API_BASE}/api/admin/contacts/export`, filters, {
      headers: authHeaders(),
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'contacts.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleOptOut = async (row) => {
    await axios.patch(
      `${API_BASE}/api/admin/contacts/opt-out`,
      { phone: row.e164 || row.phone, optedOut: !row.optedOut },
      { headers: authHeaders() }
    );
    load();
  };

  const pageCount = Math.max(Math.ceil(total / limit), 1);
  const selectedPhones = contacts
    .filter((row) => selected[row.phone] && row.valid && !row.optedOut)
    .map((row) => row.phone);
  const allPageSelected =
    contacts.length > 0 && contacts.every((row) => selected[row.phone]);

  return (
    <div className="admin-contacts" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="admin-contacts-header">
        <div>
          <h1 className="admin-page-title">{t.title}</h1>
          <p className="admin-contacts-intro">{t.intro}</p>
        </div>
        <button
          type="button"
          className="admin-contacts-lang"
          onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}
        >
          {t.lang}
        </button>
      </div>

      <div className="admin-contacts-filters">
        <label>
          <span>{t.search}</span>
          <input value={filters.q} onChange={(e) => setFilter('q', e.target.value)} />
        </label>
        <label>
          <span>{t.source}</span>
          <select value={filters.source} onChange={(e) => setFilter('source', e.target.value)}>
            <option value="all">{t.allSources}</option>
            <option value="booking">{t.booking}</option>
          </select>
        </label>
        <label>
          <span>{t.from}</span>
          <input type="date" value={filters.from} onChange={(e) => setFilter('from', e.target.value)} />
        </label>
        <label>
          <span>{t.to}</span>
          <input type="date" value={filters.to} onChange={(e) => setFilter('to', e.target.value)} />
        </label>
        <label>
          <span>{t.booked}</span>
          <select value={filters.booked} onChange={(e) => setFilter('booked', e.target.value)}>
            <option value="all">{t.anyBooking}</option>
            <option value="yes">{t.hasBooked}</option>
            <option value="no">{t.noBooking}</option>
          </select>
        </label>
        <label>
          <span>{t.validity}</span>
          <select value={filters.valid} onChange={(e) => setFilter('valid', e.target.value)}>
            <option value="all">{t.all}</option>
            <option value="valid">{t.valid}</option>
            <option value="invalid">{t.invalid}</option>
          </select>
        </label>
        <label>
          <span>{t.optOut}</span>
          <select value={filters.opted} onChange={(e) => setFilter('opted', e.target.value)}>
            <option value="all">{t.all}</option>
            <option value="in">{t.optedIn}</option>
            <option value="out">{t.optedOut}</option>
          </select>
        </label>
        <label>
          <span>{t.sort}</span>
          <select
            value={`${filters.sort}:${filters.dir}`}
            onChange={(e) => {
              const [sort, dir] = e.target.value.split(':');
              setPage(1);
              setFilters((current) => ({ ...current, sort, dir }));
            }}
          >
            <option value="lastSeen:desc">{t.lastSeen}</option>
            <option value="phone:asc">{t.phone}</option>
            <option value="name:asc">{t.name}</option>
            <option value="bookingCount:desc">{t.bookings}</option>
          </select>
        </label>
      </div>

      <div className="admin-contacts-actions">
        <button type="button" onClick={() => copyPhones(selectedPhones)}>
          <FaCopy /> {t.copySelected}
        </button>
        <button type="button" onClick={copyAll}>
          <FaCopy /> {t.copyAll}
        </button>
        <button type="button" onClick={exportCsv}>
          <FaFileExport /> {t.exportCsv}
        </button>
        {notice && <span className="admin-contacts-notice">{notice}</span>}
        {error && <span className="admin-contacts-error">{error}</span>}
      </div>

      {loading ? (
        <p>{t.loading}</p>
      ) : (
        <div className="admin-contacts-table-wrap">
          <table className="admin-contacts-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={(e) => {
                      const next = { ...selected };
                      contacts.forEach((row) => {
                        next[row.phone] = e.target.checked;
                      });
                      setSelected(next);
                    }}
                  />
                </th>
                <th>{t.phone}</th>
                <th>{t.name}</th>
                <th>{t.source}</th>
                <th>{t.bookings}</th>
                <th>{t.lastSeen}</th>
                <th>{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 && (
                <tr>
                  <td colSpan="7">{t.empty}</td>
                </tr>
              )}
              {contacts.map((row) => (
                <tr key={row.phone}>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!selected[row.phone]}
                      onChange={(e) =>
                        setSelected((current) => ({ ...current, [row.phone]: e.target.checked }))
                      }
                    />
                  </td>
                  <td>
                    <FaPhone /> {row.phone}{' '}
                    {!row.valid && <span className="admin-contacts-badge invalid">{t.invalid}</span>}
                    {row.optedOut && <span className="admin-contacts-badge opted">{t.optedOut}</span>}
                  </td>
                  <td>{row.name || '-'}</td>
                  <td>{t.booking}</td>
                  <td>{row.bookingCount}</td>
                  <td>{row.lastSeen ? row.lastSeen.slice(0, 10) : '-'}</td>
                  <td className="admin-contacts-row-actions">
                    {row.whatsapp && !row.optedOut && (
                      <a href={row.whatsapp} target="_blank" rel="noreferrer">
                        <FaWhatsapp /> {t.whatsapp}
                      </a>
                    )}
                    {row.valid && (
                      <button type="button" onClick={() => toggleOptOut(row)}>
                        {row.optedOut ? t.optedIn : t.optedOut}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-contacts-pager">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          {t.prev}
        </button>
        <span>
          {t.page} {page} {t.of} {pageCount}
        </span>
        <button type="button" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>
          {t.next}
        </button>
      </div>
    </div>
  );
};

export default AdminContacts;
